import SettingsService from "@/services/settings/SettingsService";
import LocationService from "@/services/location/LocationService";
import TerminalService from "@/services/terminal/TerminalService";

class MerchantUtil {
    getMerchant() {
        let user = JSON.parse(localStorage.getItem('user'));
        return user.merchant;
    }

    /**
     * Refresh the merchant data that is stored in localStorage at login,
     * so changes apply without logging out and in again.
     */
    refreshMerchantData(){
        this.refreshMerchantSetting();
        this.refreshLocations();
        this.refreshKdsLocations();
    }

    /**
     * The locations that have a kitchen display — a terminal of type KDS — kept on the cached user as
     * kdsLocationIds. The Kjøkkentid report is offered only where there is one: its figures are written by the
     * KDS, so anywhere else it can only ever be empty.
     *
     * Only for the roles that see Rapporter → Kjøkkentid. EMPLOYEE and ACCOUNTANT never get the entry, and
     * getTerminals is not open to ACCOUNTANT.
     */
    refreshKdsLocations(){
        let user = JSON.parse(localStorage.getItem('user'));
        if(!user || (user.userRole !== 'ADMIN' && user.userRole !== 'SUPER_ADMIN')){
            return;
        }
        const requestedFor = user.merchant;
        return TerminalService.getTerminals().then(
            (response) => {
                if(Array.isArray(response.data)){
                    let freshUser = JSON.parse(localStorage.getItem('user'));
                    // Same guard as refreshLocations: never write one merchant's terminals onto the next.
                    if(freshUser && freshUser.merchant === requestedFor){
                        freshUser.kdsLocationIds = [...new Set(response.data
                            // A KDS SUPER_ADMIN hid from the merchant (terminal/HiddenTerminals) does not count.
                            .filter(terminal => terminal.terminalType === 'KDS' && !terminal.hidden)
                            .map(terminal => terminal.locationId))];
                        localStorage.setItem('user', JSON.stringify(freshUser));
                        window.dispatchEvent(new Event('cagista:terminals-refreshed'));
                    }
                }
            },
            (error) => {
                // The last known answer stays; a failed read is not news that the KDS is gone.
                console.log(error);
            }
        );
    }

    /** The ids of the locations with a KDS, from the last refreshKdsLocations(); empty until it has answered. */
    kdsLocationIds(){
        let user = JSON.parse(localStorage.getItem('user'));
        return (user && user.kdsLocationIds) || [];
    }

    refreshLocations(){
        let user = JSON.parse(localStorage.getItem('user'));
        if(!user){
            return;
        }
        const requestedFor = user.merchant;
        return LocationService.listLocations().then(
            (response) => {
                if(response.data){
                    let freshUser = JSON.parse(localStorage.getItem('user'));
                    // Drop the answer if the session changed merchant while it was in flight. Without
                    // this, a refresh started under the previous merchant lands after the next one has
                    // logged in and writes the OLD merchant's locations onto the NEW merchant's user —
                    // which is how a freshly logged-in merchant ends up looking at someone else's
                    // locations, and how their ids leak into the requests the page makes next.
                    if(freshUser && freshUser.merchant === requestedFor){
                        freshUser.locations = response.data;
                        localStorage.setItem('user', JSON.stringify(freshUser));
                        // localStorage is not reactive: whatever derives from the locations (the
                        // Bordbookinger menu entry) has already rendered from the old copy. Say so.
                        window.dispatchEvent(new Event('cagista:locations-refreshed'));
                    }
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    /**
     * Fetch the latest merchant setting and update the logged in user in localStorage.
     * The merchant setting is stored at login, this makes setting changes apply
     * without logging out and in again.
     */
    refreshMerchantSetting(){
        let user = JSON.parse(localStorage.getItem('user'));
        if(!user){
            return;
        }
        const requestedFor = user.merchant;
        SettingsService.getMerchantSetting().then(
            (response) => {
                if(response.data){
                    let freshUser = JSON.parse(localStorage.getItem('user'));
                    // Same guard as refreshLocations: a setting fetched for the previous merchant must
                    // never be written onto the next one. merchantSetting drives which SaaS features are
                    // visible, so the wrong one shows the wrong merchant a menu they should not have.
                    if(freshUser && freshUser.merchant === requestedFor){
                        freshUser.merchantSetting = response.data;
                        localStorage.setItem('user', JSON.stringify(freshUser));
                    }
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    useFunctionality(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.useFunctionality;
    }

    // SaaS features (Website Builder, VIVA-onboarding, Abonnement og betaling + Overview-nudges).
    // Legacy merchants lack the field -> false (features hidden). Toggled per merchant on /merchant
    // (SUPER_ADMIN); new merchants are seeded with true at onboarding.
    useSaaSFeatures(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.useSaaSFeatures === true;
    }

    useAddOns(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.useAddOns;
    }

    addOnAllowMultipleTimes(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.addOnAllowMultipleTimes;
    }

    websiteShowDineInMenu(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.websiteShowDineInMenu;
    }

    showAvailableOnDays(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.showAvailableOnDays;
    }

    showAvailableOnDaysHolidayOption(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.showAvailableOnDaysHolidayOption;
    }

    enableShowAsDiscount(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.enableShowAsDiscount;
    }

    showSpicyLevelSelections(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.showSpicyLevelSelections;
    }

    getLocations(){
        let user = JSON.parse(localStorage.getItem('user'));
        let locations = user.locations;
        return locations;
    }

    userProfilePageName(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.userProfilePageName;
    }

    showUsersPage(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        if(merchantSetting.useFunctionality == 'POS' || merchantSetting.useFunctionality == 'ALL'){
            return true;
        } else {
            return false;
        }
    }

    offerDiscount(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.offerDiscount;
    }

    payForSMS(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.payForSMS;
    }

    offerRewardProgram(){
        let user = JSON.parse(localStorage.getItem('user'));
        let merchantSetting = user.merchantSetting;
        return merchantSetting.offerRewardProgram;
    }

}

export default new MerchantUtil();
